import React, { useState, useEffect } from 'react';
import { Search, Plus, Star, Clock, BookOpen, Layers, Wand2, Loader2, ArrowUpDown, Tag, CheckSquare, FileDown, FileText } from 'lucide-react';
import api from '../api/client';
import ProgrammeFormDialog from '../components/ProgrammeFormDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailSheet from '../components/DetailSheet';
import RowActions from '../components/RowActions';
import EmptyState from '../components/EmptyState';
import { SkeletonCard } from '../components/Skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { downloadPdf } from '../utils/export';
import { nsfLabel } from '../constants/nsf';

const LEVEL_STYLES = {
  'Débutant': 'bg-[#E6FBF7] text-[#0F766E]',
  'Intermédiaire': 'bg-[#FEF3E7] text-[#B45309]',
  'Avancé': 'bg-[#FDF4FF] text-[#A21CAF]',
  'Tout public': 'bg-[#EEF2FF] text-[#4F46E5]',
};

export default function Programmes() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Tous');
  const [programs, setPrograms] = useState([]);
  const [ratings, setRatings] = useState({}); // { programme_id: { avg, count } }
  const [create, setCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selected, setSelected] = useState(new Set());
  const [cats, setCats] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sessionCounts, setSessionCounts] = useState({});
  // Ce que les fiches ne disent pas encore de leur formation. Les 41 fiches PDF
  // imprimaient le meme texte generique pour les methodes, l'evaluation et
  // l'accessibilite, et aucun ecran ne le disait.
  const [fiches, setFiches] = useState(null);

  useEffect(() => { api.get('/programs').then(({ data }) => setPrograms(data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { api.get('/categories').then(({ data }) => setCats(data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/analytics/programs-ratings').then(({ data }) => setRatings(data || {})).catch(() => {}); }, []);
  useEffect(() => { api.get('/analytics/programs-session-counts').then(({ data }) => setSessionCounts(data || {})).catch(() => {}); }, []);
  const chargerFiches = () => api.get('/programmes/fiches-qualiopi').then(({ data }) => setFiches(data)).catch(() => {});
  useEffect(() => { chargerFiches(); }, []);

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleBulkCategory = async (categoryId) => {
    const program_ids = Array.from(selected);
    try {
      const { data } = await api.post('/programs/bulk-assign-category', { program_ids, category_id: categoryId });
      setPrograms((prev) => prev.map((p) => selected.has(p.id) ? { ...p, category: data.category, category_id: categoryId } : p));
      toast({ title: 'Catégorie assignée', description: `${data.modified} programme(s) → ${data.category}` });
      setSelected(new Set());
      setBulkOpen(false);
    } catch {
      toast({ title: 'Erreur', description: 'Assignation impossible.' });
    }
  };

  const handleAiGenerate = async () => {
    if (!aiBrief.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post('/ai/program/generate', { brief: aiBrief });
      // **Aucune note inventee** (2026-11-02, « enlever des notes non
      // reelles »). Cette ligne posait `rating: 4.5` — quatre etoiles et demie
      // sur une formation qui n'a jamais ete donnee —, et `sessions: 0` sur un
      // champ que plus personne n'ecrit. La vraie note vient de
      // `/analytics/programs-ratings`, calculee depuis les evaluations des
      // apprenants, et le vrai compte de sessions de
      // `/analytics/programs-session-counts`. Un chiffre pose a la main ne
      // pouvait que les contredire. La couleur reste : elle vient de la
      // categorie, et ce defaut n'est qu'un repli.
      const payload = { ...data, color: '#4F46E5' };
      const res = await api.post('/programs', payload);
      setPrograms((p) => [...p, res.data]);
      toast({ title: '✨ Programme généré par OFia', description: res.data.title });
      setAiOpen(false); setAiBrief('');
    } catch (e) {
      toast({ title: 'Erreur', description: "OFia n'a pas pu générer le programme." });
    }
    setAiLoading(false);
  };

  // La bande se relit apres chaque enregistrement : remplir ce qui manquait
  // doit faire disparaitre le reproche, sinon on le corrige deux fois.
  const handleCreated = (data) => { setPrograms((p) => [...p, data]); chargerFiches(); };
  const handleUpdated = (data) => { setPrograms((p) => p.map((x) => x.id === data.id ? data : x)); chargerFiches(); };
  const handleDelete = async () => {
    await api.delete(`/programs/${deleting.id}`);
    setPrograms((p) => p.filter((x) => x.id !== deleting.id));
    toast({ title: 'Programme supprimé' });
  };

  const [generating, setGenerating] = useState(null);

  // **Deux documents, deux boutons** (2026-11-02, « le PDF est l'ancien »). Un
  // seul geste servait les deux, et il preferait le PDF depose : « the official
  // document always wins ». La regle est bonne — ce sont de vrais documents, 42
  // noms de fichier distincts deposes a la main — mais comme les 42 fiches en
  // portent un, **la fiche editee n'etait servie pour aucune**, et le bouton qui
  // l'edite ne s'affichait nulle part. Deux jours de travail sur ce document
  // etaient injoignables depuis cet ecran.
  const telechargerPdfDepose = async (p) => {
    setGenerating(p.id);
    try {
      await downloadPdf(api, `/programs/${p.id}/uploaded-pdf`, p.uploaded_pdf_filename || `programme-${p.id}`);
      toast({ title: 'PDF téléchargé', description: p.uploaded_pdf_filename });
    } catch (e) {
      toast({ title: 'Erreur', description: 'Téléchargement impossible pour le moment.' });
    } finally {
      setGenerating(null);
    }
  };

  // Celui-la edite la fiche depuis les champs du programme, sans condition : un
  // PDF depose ne l'empeche plus, il est juste l'autre bouton.
  const editerFiche = async (p) => {
    setGenerating(p.id);
    try {
      await downloadPdf(api, `/programs/${p.id}/datasheet/pdf`, `fiche-${p.id}`);
      toast({ title: 'Fiche programme éditée', description: p.title });
    } catch (e) {
      toast({ title: 'Erreur', description: "L'édition de la fiche a échoué." });
    } finally {
      setGenerating(null);
    }
  };

  const categories = ['Tous', ...(cats && cats.length ? cats.map((c) => c.name) : [...new Set(programs.map((p) => p.category).filter(Boolean))])];

  // **La couleur d'une carte vient de sa categorie** (2026-11-02). Le champ
  // `color` du programme existait, valait `#4F46E5` sur les quarante-deux, et
  // **aucun ecran ne permettait d'en changer** : les cartes etaient donc toutes
  // du meme indigo. Les categories, elles, portent deja une couleur chacune et
  // elle se regle dans Parametres — six teintes distinctes au lieu d'une.
  // Le champ du programme reste le repli, pour les fiches d'un organisme qui
  // n'aurait pas range ses categories.
  const couleurDe = (p) =>
    cats.find((c) => c.name === p.category)?.color || p.color || '#4F46E5';
  const filtered = programs.filter((p) => {
    const title = p.title || p.name || '';
    const ok = title.toLowerCase().includes(search.toLowerCase());
    const okCat = category === 'Tous' || p.category === category;
    return ok && okCat;
  }).sort((a, b) => {
    // **« Mieux notes » et « Plus populaires » ne triaient rien** (2026-11-02).
    // Le comparateur lisait `a[sort.key]`, donc `a.rating` et `a.sessions` —
    // les deux champs valent **0 sur les quarante-deux fiches**, et l'ordre
    // restait celui d'avant le clic. Les vraies valeurs sont deja chargees par
    // cet ecran : la note calculee depuis les evaluations, et le compte reel de
    // sessions. Une fiche sans evaluation descend en bas plutot que de se
    // melanger aux autres : -1, parce que zero est une note possible.
    const cle = (p) => sort.key === 'rating' ? (ratings[p.id]?.count > 0 ? ratings[p.id].avg : -1)
      : sort.key === 'sessions' ? (sessionCounts[p.id] ?? 0)
      : (p[sort.key] ?? '');
    const va = cle(a); const vb = cle(b);
    if (typeof va === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
    return sort.dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un programme…" className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-[#EFF0F6] text-[13.5px] focus:border-[#C7D2FE] outline-none" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} className={`h-9 px-3.5 rounded-xl text-[12px] font-semibold transition-colors ${category === c ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-white border border-[#EFF0F6] text-[#6B6C85] hover:border-[#C7D2FE]'}`}>{c}</button>
          ))}
        </div>
        <button onClick={() => setAiOpen(true)} className="h-10 px-4 rounded-xl bg-white border border-[#C7D2FE] text-[13px] font-semibold text-[#4F46E5] flex items-center gap-2 shadow-sm md:ml-auto hover:bg-[#EEF2FF]">
          <Wand2 className="w-4 h-4" /> Générer avec IA
        </button>
        <div className="flex items-center gap-1 h-10 px-3 rounded-xl bg-white border border-[#EFF0F6] text-[12.5px] font-medium text-[#4A4B63]">
          <ArrowUpDown className="w-3.5 h-3.5 text-[#8E8FA5]" />
          <select value={`${sort.key}:${sort.dir}`} onChange={(e) => { const [k, d] = e.target.value.split(':'); setSort({ key: k, dir: d }); }} className="bg-transparent outline-none cursor-pointer">
            <option value="title:asc">Titre ↑</option>
            <option value="title:desc">Titre ↓</option>
            <option value="price:asc">Prix ↑</option>
            <option value="price:desc">Prix ↓</option>
            <option value="rating:desc">Mieux notés</option>
            <option value="sessions:desc">Plus populaires</option>
          </select>
        </div>
        <button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95">
          <Plus className="w-4 h-4" /> Nouveau programme
        </button>
      </div>

      {fiches && fiches.fiches_incompletes > 0 && (
        <div data-testid="bande-fiches-qualiopi" className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3">
          <div className="text-[13px] font-semibold text-[#92400E]">
            {fiches.fiches_incompletes} fiche{fiches.fiches_incompletes > 1 ? 's' : ''} sur {fiches.total} ne décri{fiches.fiches_incompletes > 1 ? 'vent' : 't'} pas encore la formation
          </div>
          <div className="text-[11.5px] text-[#92400E]/80 mt-0.5">
            Le document imprime alors un texte générique — les mêmes méthodes et la même évaluation pour toutes les formations.
            {fiches.gabarits_poses.length === 0 && ' Aucun gabarit n’est posé dans Paramètres.'}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {fiches.fiches.filter((f) => f.manques.length).slice(0, 6).map((f) => {
              const prog = (programs || []).find((x) => x.id === f.id);
              return (
                <button
                  key={f.id}
                  onClick={() => prog && setEditing(prog)}
                  className="h-7 px-2.5 rounded-lg bg-white border border-[#FDE68A] text-[11.5px] font-medium text-[#92400E] hover:bg-[#FEF3C7]"
                  title={f.manques.map((m) => m.libelle).join(' · ')}
                >
                  {f.title} <span className="text-[#B45309]">· {f.manques.length}</span>
                </button>
              );
            })}
            {fiches.fiches_incompletes > 6 && (
              <span className="h-7 px-2 inline-flex items-center text-[11.5px] text-[#92400E]/70">
                + {fiches.fiches_incompletes - 6} autres
              </span>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {loading && Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full">
            <EmptyState
              icon={BookOpen}
              title="Aucun programme"
              description="Créez votre premier programme de formation pour démarrer, ou laissez OFia le générer pour vous."
              action={
                <div className="flex gap-2">
                  <button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold">Créer un programme</button>
                  <button onClick={() => setAiOpen(true)} className="h-10 px-4 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold flex items-center gap-1.5"><Wand2 className="w-4 h-4" /> Avec IA</button>
                </div>
              }
            />
          </div>
        )}
        {filtered.map((p) => (
          <div key={p.id} onClick={() => setDetail(p)} className={`soft-card overflow-hidden cursor-pointer group relative ${selected.has(p.id) ? 'ring-2 ring-[#4F46E5]' : ''}`}>
            <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
              <input
                data-testid={`program-select-${p.id}`}
                type="checkbox"
                checked={selected.has(p.id)}
                onChange={() => toggleSelect(p.id)}
                className="w-4 h-4 rounded accent-[#4F46E5] cursor-pointer bg-white shadow"
              />
            </div>
            <div className="absolute top-2 right-2 z-10 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              {p.uploaded_pdf_filename && (
                <span
                  data-testid={`program-pdf-indicator-${p.id}`}
                  title={`PDF officiel attaché : ${p.uploaded_pdf_filename}`}
                  className="w-7 h-7 rounded-lg bg-[#E6FBF7] text-[#0F766E] border border-[#A7F3D0] flex items-center justify-center"
                >
                  <FileText className="w-3.5 h-3.5" />
                </span>
              )}
              <RowActions onView={() => setDetail(p)} onEdit={() => setEditing(p)} onDelete={() => setDeleting(p)} />
            </div>
            <div className="h-28 relative" style={{ background: `linear-gradient(135deg, ${couleurDe(p)}22 0%, ${couleurDe(p)}44 100%)` }}>
              <div className="absolute inset-0 flex items-center justify-center">
                <Layers className="w-10 h-10" style={{ color: couleurDe(p) }} strokeWidth={1.5} />
              </div>
              <span className={`absolute top-3 left-3 text-[10.5px] font-semibold px-2 py-0.5 rounded ${LEVEL_STYLES[p.level] || 'bg-white text-[#4A4B63]'}`}>{p.level}</span>
            </div>
            <div className="p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: couleurDe(p) }}>{p.category}</div>
              <h3 className="text-[14.5px] font-semibold text-[#1A1B2E] mt-1 leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{p.title}</h3>
              {p.nsf_code && (
                <div className="mt-1 inline-flex items-center gap-1 text-[10.5px] font-medium text-[#6B6C85] bg-[#F6F7FB] border border-[#EFF0F6] rounded px-1.5 py-0.5" title={nsfLabel(p.nsf_code)}>
                  NSF {p.nsf_code}
                </div>
              )}
              <p className="text-[12px] text-[#6B6C85] mt-1 line-clamp-2 leading-snug">{p.description}</p>
              <div className="flex items-center gap-3 mt-3 text-[11.5px] text-[#6B6C85]">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.duration}h</span>
                <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {sessionCounts[p.id] ?? p.sessions ?? 0} sessions</span>
                <span className="flex items-center gap-1" title={ratings[p.id]?.count ? `${ratings[p.id].count} évaluation(s)` : 'Aucune évaluation'}>
                  <Star className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" />
                  {ratings[p.id]?.count > 0 ? ratings[p.id].avg : '—'}
                </span>
              </div>
              <div className="mt-3 pt-3 border-t border-[#F3F4F8] flex items-center justify-between">
                <div className="text-[16px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{p.price?.toLocaleString('fr-FR')} €</div>
                <button onClick={(e) => { e.stopPropagation(); setDetail(p); }} className="text-[12px] font-semibold text-[#4F46E5] hover:underline">Détails →</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ProgrammeFormDialog
        open={create}
        onOpenChange={setCreate}
        mode="create"
        categories={cats}
        onCreated={handleCreated}
      />
      <ProgrammeFormDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        mode="edit"
        initialValues={editing}
        categories={cats}
        realRating={editing ? ratings[editing.id] : null}
        onUpdated={handleUpdated}
      />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Supprimer ce programme ?" description={deleting?.title} onConfirm={handleDelete} />

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-[520px] rounded-2xl">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg brand-gradient flex items-center justify-center"><Wand2 className="w-4 h-4 text-white" /></div>
              <DialogTitle style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Générer un programme avec OFia</DialogTitle>
            </div>
            <DialogDescription>Décrivez en quelques mots le programme souhaité, OFia s'occupe du reste.</DialogDescription>
          </DialogHeader>
          <textarea
            value={aiBrief}
            onChange={(e) => setAiBrief(e.target.value)}
            rows={4}
            placeholder="Ex. Formation intensive de 3 jours sur Python pour data analysts débutants, avec exercices pratiques pandas et matplotlib."
            className="w-full px-3 py-2 mt-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none resize-none"
          />
          <div className="flex gap-2 pt-2">
            <button onClick={() => setAiOpen(false)} className="flex-1 h-10 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE]">Annuler</button>
            <button onClick={handleAiGenerate} disabled={aiLoading || !aiBrief.trim()} className="flex-1 h-10 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-2 shadow-sm disabled:opacity-60">
              {aiLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {aiLoading ? 'Génération…' : 'Générer'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <DetailSheet
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        title={detail?.title}
        subtitle={detail?.category}
        rows={detail ? [
          { label: 'Niveau', value: detail.level },
          { label: 'Spécialité NSF', value: detail.nsf_code ? nsfLabel(detail.nsf_code) : '—' },
          { label: 'Durée', value: `${detail.duration}h` },
          { label: 'Prix', value: `${detail.price?.toLocaleString('fr-FR')} €` },
          { label: 'Mode', value: detail.mode || '—' },
          { label: 'Formateur', value: detail.trainer || '—' },
          { label: 'Lieu / Adresse', value: detail.location || '—' },
          { label: 'Public cible', value: detail.public_cible || '—' },
          { label: 'Sessions', value: sessionCounts[detail.id] ?? detail.sessions ?? 0 },
          { label: 'Note moyenne (auto)', value: ratings[detail.id]?.count > 0 ? `${ratings[detail.id].avg} / 5 · ${ratings[detail.id].count} éval.` : 'Aucune évaluation' },
          { label: 'Description', value: detail.description || '—' },
          { label: 'Objectifs', value: detail.objectives || '—' },
          { label: 'Prérequis', value: detail.prerequisites || '—' },
          detail.programme_pdf_url ? { label: 'PDF programme', value: (<a href={detail.programme_pdf_url} target="_blank" rel="noreferrer" className="text-[#4F46E5] underline font-semibold">Ouvrir le PDF →</a>) } : null,
          detail.uploaded_pdf_filename ? { label: 'PDF uploadé', value: (
            <span className="inline-flex items-center gap-2 text-[12.5px] text-[#0F766E] bg-[#E6FBF7] border border-[#A7F3D0] rounded-lg px-2 py-1">
              <FileDown className="w-3.5 h-3.5" />
              {detail.uploaded_pdf_filename} · {Math.round((detail.uploaded_pdf_size || 0) / 1024)} Ko
            </span>
          ) } : null,
        ].filter(Boolean) : []}
      >
        {detail && (
          <div className="mt-4 pt-4 border-t border-[#EFF0F6]">
            {detail.uploaded_pdf_filename && (
              <button
                data-testid={`prog-download-uploaded-pdf-${detail.id}`}
                onClick={() => telechargerPdfDepose(detail)}
                disabled={generating === detail.id}
                className="w-full h-11 rounded-xl bg-[#0F766E] text-white text-[13.5px] font-semibold flex items-center justify-center gap-2 shadow-sm hover:bg-[#0E6D66] disabled:opacity-60"
              >
                {generating === detail.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                {generating === detail.id ? 'Téléchargement…' : `Télécharger le PDF officiel (${detail.uploaded_pdf_filename})`}
              </button>
            )}
            <button
              data-testid={`prog-ai-datasheet-${detail.id}`}
              onClick={() => editerFiche(detail)}
              disabled={generating === detail.id}
              className={detail.uploaded_pdf_filename
                ? 'w-full h-11 mt-2 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13.5px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60'
                : 'w-full h-11 rounded-xl brand-gradient text-white text-[13.5px] font-semibold flex items-center justify-center gap-2 shadow-sm disabled:opacity-60'}
            >
              {generating === detail.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {generating === detail.id ? 'Édition en cours…' : 'Télécharger la fiche éditée (PDF)'}
            </button>
            <div className="text-[11px] text-[#8E8FA5] text-center mt-2">
              {/* « Editer » laissait croire a un affichage : les deux boutons
                  telechargent un fichier, et le second le dit maintenant comme
                  le premier. */}
              {detail.uploaded_pdf_filename
                ? 'Deux documents : celui que vous avez déposé, et la fiche éditée depuis les champs de ce programme. Les deux se téléchargent.'
                : 'Vos champs, puis vos textes communs à défaut · aux couleurs de votre charte'}
            </div>
          </div>
        )}
      </DetailSheet>

      {selected.size > 0 && (
        <div data-testid="program-bulk-bar" className="fixed left-1/2 -translate-x-1/2 bottom-6 z-30 bg-[#1A1B2E] text-white rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          <button onClick={() => setSelected(new Set())} className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center">✕</button>
          <div className="text-[13px] font-semibold">{selected.size} programme(s) sélectionné(s)</div>
          <div className="h-5 w-px bg-white/15 mx-1" />
          <button data-testid="program-bulk-assign-btn" onClick={() => setBulkOpen(true)} className="h-9 px-3 rounded-lg brand-gradient text-[13px] font-semibold flex items-center gap-1.5">
            <Tag className="w-4 h-4" /> Assigner une catégorie
          </button>
        </div>
      )}

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-[460px] rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Assigner une catégorie</DialogTitle>
            <DialogDescription>{selected.size} programme(s) recevront la catégorie sélectionnée.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[320px] overflow-auto py-2">
            {cats.length === 0 && <div className="text-[13px] text-[#8E8FA5]">Aucune catégorie créée. Rendez-vous dans Paramètres → Catégories.</div>}
            {cats.map((c) => (
              <button
                key={c.id}
                data-testid={`bulk-cat-${c.id}`}
                onClick={() => handleBulkCategory(c.id)}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#F3F4F8] hover:bg-[#EEF2FF] hover:border-[#C7D2FE] transition-colors"
              >
                <span className="w-3 h-3 rounded-sm" style={{ background: c.color || '#4F46E5' }} />
                <span className="text-[13.5px] font-semibold text-[#1A1B2E]">{c.name}</span>
                {c.description && <span className="text-[12px] text-[#8E8FA5] ml-1 truncate">· {c.description}</span>}
                <CheckSquare className="w-4 h-4 text-[#8E8FA5] ml-auto" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
